import { useQuery } from '@tanstack/react-query';
import { getListRows, getTotalPages } from '@/shared/lib/pagination';
import { useListFilters } from '@/shared/lib/useListFilters';
import { FilterActions } from '@/shared/ui/FilterActions';
import { FilterField } from '@/shared/ui/FilterField';
import { Pagination } from '@/shared/ui/Pagination';
import { QueryStatus } from '@/shared/ui/QueryStatus';
import { SearchKeyInput } from '@/shared/ui/SearchKeyInput';
import { SortableTh } from '@/shared/ui/SortableTh';
import { ListFilterControl } from './ListFilterControl';
import { buildEmptyFilters, buildListParams, hasAppliedFilter } from './listParams';
import type { FilterValue, ListFilter, ListPageConfig, ListResponse, ListRow } from './types';

const getPageSize = (data: ListResponse | undefined) => {
  const pagination = data?.pagination as { pageSize?: unknown } | undefined;
  return Number(pagination?.pageSize) || 0;
};

/**
 * Config-driven admin list page: filter form (search-by-key + filters), sortable table, pagination.
 * See `ListPageConfig` for what a page has to describe.
 */
export function GenericListPage<T extends ListRow>({ config }: { config: ListPageConfig<T> }) {
  const { searchKeys, filters = [], columns } = config;
  const list = useListFilters(buildEmptyFilters(config));
  const { draft, applied, page, sortField, sortOrder } = list;

  const params = buildListParams(config, { applied, page, sortField, sortOrder });
  const { data, isLoading, isFetching, error } = useQuery({
    queryKey: [config.queryKey, params],
    queryFn: () => config.fetchList(params) as Promise<ListResponse>,
    enabled: !config.requireFilter || hasAppliedFilter(applied),
  });

  const rawRows = !data
    ? []
    : typeof config.rows === 'function'
      ? config.rows(data)
      : getListRows<T>(data, config.rows);
  const rows = config.selectRows
    ? config.selectRows(rawRows, { applied, sortField, sortOrder })
    : rawRows;
  const rowOffset = (page - 1) * getPageSize(data);

  // Changing a filter clears the filters that depend on it, transitively (e.g. country → region → city).
  const dependentNames = (name: string): string[] =>
    filters.filter((f) => f.dependsOn === name).flatMap((f) => [f.name, ...dependentNames(f.name)]);

  const changeFilter = (filter: ListFilter, value: FilterValue) =>
    list.patchDraft({
      [filter.name]: value,
      ...Object.fromEntries(dependentNames(filter.name).map((dependent) => [dependent, ''])),
      ...filter.patchOnChange?.(String(value)),
    });

  return (
    <>
      <section className="content-header">
        <h1>{config.title}</h1>
        {config.headerActions && (
          <div className="row">
            <div className="col-sm-12">{config.headerActions}</div>
          </div>
        )}
      </section>
      <section className="content">
        <div className="box">
          {(searchKeys || filters.length > 0) && (
            <div className="box-header with-border">
              {config.boxTitle && <h3 className="box-title">{config.boxTitle}</h3>}
              <form onSubmit={list.submit} style={{ marginTop: 12 }}>
                {searchKeys && (
                  <FilterField label="Поиск">
                    <SearchKeyInput
                      keyOptions={searchKeys}
                      searchKey={String(draft.searchKey)}
                      searchValue={String(draft.searchValue)}
                      onKeyChange={(v) => list.setField('searchKey', v)}
                      onValueChange={(v) => list.setField('searchValue', v)}
                    />
                  </FilterField>
                )}
                {filters.map((filter) => (
                  <FilterField key={filter.name} label={filter.label}>
                    <ListFilterControl
                      filter={filter}
                      draft={draft}
                      listKey={config.queryKey}
                      onChange={(value) => changeFilter(filter, value)}
                    />
                  </FilterField>
                ))}
                <FilterActions disabled={isFetching} onReset={list.reset} hideReset={config.hideReset} />
              </form>
            </div>
          )}
          <div className="box-body">
            <QueryStatus isLoading={isLoading} error={error} errorText={config.errorText} />
            {data && config.summary?.(data)}
            <div style={{ overflowX: 'auto' }}>
              <table className="table table-bordered table-hover">
                <thead>
                  <tr>
                    {columns.map((column) =>
                      column.sortField ? (
                        <SortableTh
                          key={column.header}
                          active={sortField === column.sortField}
                          onClick={() => list.toggleSort(column.sortField as string)}
                        >
                          {column.header}
                        </SortableTh>
                      ) : (
                        <th key={column.header}>{column.header}</th>
                      ),
                    )}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, index) => (
                    <tr key={String(row.id ?? index)}>
                      {columns.map((column) => (
                        <td key={column.header}>
                          {column.render(row, { index, number: rowOffset + index + 1 })}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {config.paginated !== false && (
              <Pagination page={page} totalPages={getTotalPages(data)} onChange={list.setPage} />
            )}
          </div>
        </div>
      </section>
    </>
  );
}
